import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trophy, Medal, Award } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface Player {
  position: number;
  name: string;
  partner: string;
  wins?: number;
  losses?: number;
  setsFor?: number;
  setsAgainst?: number;
  gamesFor: number;
  gamesAgainst: number;
  difference: number;
  isCurrentUser: boolean;
}

interface LeagueRankingTableProps {
  players: Player[];
  modalidad: 'singles' | 'doubles';
  type: 'round-robin' | 'sumatoria-juegos';
  sport?: 'tennis' | 'padel' | 'pickleball';
}

const LeagueRankingTable = ({ players, modalidad, type, sport = 'padel' }: LeagueRankingTableProps) => {
  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Trophy className="h-4 w-4 text-amber-500" />;
      case 2:
        return <Medal className="h-4 w-4 text-gray-400" />;
      case 3:
        return <Award className="h-4 w-4 text-amber-600" />;
      default:
        return null;
    }
  };

  const getPositionBadge = (position: number) => {
    if (position <= 3) {
      return (
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
          Podio
        </Badge>
      );
    }
    return null;
  };

  const isMobile = useIsMobile();
  const isPadel = sport.toLowerCase() === 'pádel';
  
  // Mobile card component for better mobile experience
  const MobilePlayerCard = ({ player }: { player: Player }) => (
    <Card className={`${
      player.isCurrentUser ? 'border-l-4 border-l-primary bg-primary/5' : ''
    } ${
      player.position <= 3 ? 'bg-gradient-to-r from-amber-50 to-amber-100' : ''
    }`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              {getPositionIcon(player.position)}
              <span className="text-lg font-bold">{player.position}</span>
              {getPositionBadge(player.position)}
            </div>
          </div>
          {type === 'round-robin' && (
            <Badge variant="outline" className="bg-primary/10 text-primary font-bold">
              {(player.wins || 0) * 3} pts
            </Badge>
          )}
        </div>
        
        <div className="space-y-2">
          <div className={`font-medium text-base ${player.isCurrentUser ? 'text-primary' : ''}`}>
            {isPadel || modalidad === 'doubles' ? (
              <>
                <div>{player.name}</div>
                <div className="text-sm text-gray-500">{player.partner}</div>
              </>
            ) : (
              <div>{player.name}</div>
            )}
            {player.isCurrentUser && <Badge variant="outline" className="ml-2 text-xs">TÚ</Badge>}
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="space-y-1">
              {type === 'round-robin' && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Victorias:</span>
                  <span className="font-medium text-green-600">{player.wins}</span>
                </div>
              )}
              {type === 'round-robin' && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sets AF:</span>
                  <span className="font-bold text-primary">{player.setsFor || 0}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Juegos AF:</span>
                <span className="font-bold text-primary">{player.gamesFor}</span>
              </div>
            </div>
            <div className="space-y-1">
              {type === 'round-robin' && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Derrotas:</span>
                  <span className="font-medium text-red-600">{player.losses}</span>
                </div>
              )}
              {type === 'round-robin' && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sets EC:</span>
                  <span className="text-muted-foreground">{player.setsAgainst || 0}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Juegos EC:</span>
                <span className="text-muted-foreground">{player.gamesAgainst}</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            <span className="text-gray-600">Diferencia:</span>
            <span className={`font-bold ${
              player.difference > 0 ? 'text-green-600' : 
              player.difference < 0 ? 'text-red-600' : 'text-gray-600'
            }`}>
              {player.difference > 0 ? '+' : ''}{player.difference}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader className={isMobile ? 'p-4' : 'p-6'}>
        <CardTitle className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-gray-900 flex items-center gap-2`}>
          <Trophy className="h-5 w-5 text-amber-500" />
          Tabla de Posiciones
        </CardTitle>
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-3 rounded-lg border border-primary/20">
          {type === 'round-robin' ? (
            <>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-primary font-medium`}>
                🏆 Sistema: 3 puntos por victoria, 0 por derrota
              </p>
                 <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-primary/80 mt-1`}>
                Desempate: 1) Partidos ganados 2) Diferencia de sets 3) Diferencia de juegos
              </p>
            </>
          ) : (
            <>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-primary font-medium`}>
                🎯 Sistema: Total de juegos sumados
              </p>
              <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-primary/80 mt-1`}>
                Desempate: 1) Juegos a favor 2) Diferencia
              </p>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className={isMobile ? 'p-4 pt-0' : 'p-6 pt-0'}>
        {isMobile ? (
          <div className="space-y-3">
            {players.map((player) => (
              <MobilePlayerCard key={player.position} player={player} />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Pos</TableHead>
                  <TableHead>{isPadel ? 'Pareja' : (modalidad === 'doubles' ? 'Pareja' : 'Jugador')}</TableHead>
                   {type === 'round-robin' && (
                     <>
                       <TableHead className="text-center">V</TableHead>
                       <TableHead className="text-center">D</TableHead>
                     </>
                   )}
                   {type === 'round-robin' && (
                     <>
                       <TableHead className="text-center">SF</TableHead>
                       <TableHead className="text-center">SC</TableHead>
                     </>
                   )}
                   <TableHead className="text-center">JF</TableHead>
                   <TableHead className="text-center">JC</TableHead>
                   <TableHead className="text-center">Dif</TableHead>
                   {type === 'round-robin' && <TableHead className="text-center">Pts</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {players.map((player) => (
                  <TableRow 
                    key={player.position}
                    className={`transition-colors hover:bg-gray-50 ${
                      player.isCurrentUser ? 'bg-primary/5 border-l-4 border-l-primary' : ''
                    } ${
                      player.position <= 3 ? 'bg-gradient-to-r from-amber-50 to-amber-100' : ''
                    }`}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center space-x-2">
                        {getPositionIcon(player.position)}
                        <span className="text-lg">{player.position}</span>
                        {getPositionBadge(player.position)}
                      </div>
                    </TableCell>
                    <TableCell className={`font-medium ${player.isCurrentUser ? 'text-primary' : ''}`}>
                      {isPadel || modalidad === 'doubles' ? (
                        <div className="space-y-1">
                          <div>{player.name}</div>
                          <div className="text-sm text-gray-500">{player.partner}</div>
                        </div>
                      ) : (
                        <div>{player.name}</div>
                      )}
                      {player.isCurrentUser && <Badge variant="outline" className="ml-2 text-xs">TÚ</Badge>}
                    </TableCell>
                     {type === 'round-robin' && (
                       <>
                         <TableCell className="text-center font-medium text-green-600">{player.wins}</TableCell>
                         <TableCell className="text-center font-medium text-red-600">{player.losses}</TableCell>
                       </>
                     )}
                     {type === 'round-robin' && (
                       <>
                         <TableCell className="text-center font-medium">{player.setsFor || 0}</TableCell>
                         <TableCell className="text-center font-medium">{player.setsAgainst || 0}</TableCell>
                       </>
                     )}
                     <TableCell className="text-center font-bold text-primary">{player.gamesFor}</TableCell>
                    <TableCell className="text-center">{player.gamesAgainst}</TableCell>
                    <TableCell className={`text-center font-bold ${
                      player.difference > 0 ? 'text-green-600' : 
                      player.difference < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {player.difference > 0 ? '+' : ''}{player.difference}
                    </TableCell>
                    {type === 'round-robin' && (
                      <TableCell className="text-center font-bold text-primary">
                        {(player.wins || 0) * 3}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LeagueRankingTable;